import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  useCreateHubHourAdjustment,
  useDeleteHubHourAdjustment,
  useHubHourAdjustments,
  useUpdateHubHourAdjustment,
} from "@/hooks/useHubHourAdjustments";
import {
  useAuthContext,
  useCreateHubHourAdjustmentForm,
  usePermissions,
} from "@niutraan/hooks";
import {
  PermissionEnum,
  type HubEnrollmentDto,
  type HubHourAdjustmentDto,
} from "@niutraan/shared-dto";
import { Edit, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

interface HubEnrollmentDetailProps {
  enrollment: HubEnrollmentDto;
  enrollmentId: string;
  showHourAdjustments?: boolean;
  onRefreshAdjustments?: () => void;
}

export function HubEnrollmentDetail({
  enrollment,
  enrollmentId,
  showHourAdjustments = true,
  onRefreshAdjustments,
}: HubEnrollmentDetailProps) {
  const { user } = useAuthContext();
  const { hasPermission } = usePermissions();

  // Check specific permissions
  const canReadHourAdjustments = hasPermission(
    PermissionEnum.HUB_HOUR_ADJUSTMENT_READ,
  );
  const canCreateHourAdjustments = hasPermission(
    PermissionEnum.HUB_HOUR_ADJUSTMENT_WRITE,
  );
  const canUpdateHourAdjustments = hasPermission(
    PermissionEnum.HUB_HOUR_ADJUSTMENT_UPDATE,
  );
  const canDeleteHourAdjustments = hasPermission(
    PermissionEnum.HUB_HOUR_ADJUSTMENT_DELETE,
  );

  const [tab, setTab] = useState("details");
  const [openDialog, setOpenDialog] = useState(false);
  const [editItem, setEditItem] = useState<any>(null);

  const {
    form,
    handleSubmit,
    isSubmitting,
    hourAdjustments,
    refetchAdjustments,
  } = useCreateHubHourAdjustmentForm(enrollmentId, {
    useHubHourAdjustments,
    useCreateHubHourAdjustment,
    useUpdateHubHourAdjustment,
    onSuccess: () => {
      setOpenDialog(false);
      form.setValue("hours", 0);
      form.setValue("reason", "");
      toast.success(
        `Hour adjustment ${editItem ? "updated" : "created"} successfully`,
      );
      if (onRefreshAdjustments) {
        onRefreshAdjustments();
      }
    },
    onError: (error: any) =>
      toast.error(
        `Failed to save hour adjustment: ${(error as Error).message}`,
      ),
  });

  const { control } = form as any;

  const deleteAdjustmentMutation = useDeleteHubHourAdjustment();

  form.setValue("hubEnrollmentId", enrollmentId);
  form.setValue("userId", user?.id ?? "");

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Check if user is currently using the Hub (has active usage)
  const hasActiveUsage = () => {
    if (!enrollment?.usages || enrollment.usages.length === 0) {
      return false;
    }

    // Check for any usage that has a startTime but no endTime
    return enrollment.usages.some((usage) => usage.startTime && !usage.endTime);
  };

  // Calculate total adjusted hours
  const calculateTotalAdjustedHours = () => {
    if (!hourAdjustments || hourAdjustments?.length === 0) return 0;
    return hourAdjustments.reduce(
      (total: number, adjustment: HubHourAdjustmentDto) =>
        total + adjustment.hours,
      0,
    );
  };

  // Calculate hours used based on usages and adjustments
  const calculateHoursUsed = () => {
    // Start with 0 as base
    let hoursUsed = 0;

    // Add hours from usages if available
    if (enrollment?.usages && enrollment.usages.length > 0) {
      // Use durationHours directly from each usage record
      enrollment.usages.forEach((usage) => {
        if (usage.durationHours) {
          hoursUsed += usage.durationHours;
        }
      });
    }

    // Only apply negative hour adjustments (which represent additional usage)
    // Positive adjustments are extra hours added to the plan, not hours used
    if (hourAdjustments && hourAdjustments.length > 0) {
      hourAdjustments.forEach((adjustment) => {
        // Only count negative adjustments as additional usage
        if (adjustment.hours < 0) {
          hoursUsed += Math.abs(adjustment.hours);
        }
      });
    }

    return Math.max(0, hoursUsed); // Ensure we don't return negative hours used
  };

  const openAdjustmentModal = (item: any) => {
    form.setValue("editingId", item.id);
    form.setValue("hours", item.hours);
    form.setValue("reason", item.reason);
    setEditItem(item);
    setOpenDialog(true);
  };

  return (
    <>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList
          className={
            showHourAdjustments
              ? "grid grid-cols-2 w-full"
              : "grid grid-cols-1 w-full"
          }
        >
          <TabsTrigger value="details">Details</TabsTrigger>
          {showHourAdjustments && (
            <TabsTrigger value="adjustments">Hour Adjustments</TabsTrigger>
          )}
        </TabsList>

        {/* -------- DETAILS TAB -------- */}
        <TabsContent value="details" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Enrollment Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between text-sm">
                <span>Current Usage:</span>
                <span className="text-muted-foreground">
                  {hasActiveUsage() ? "In Use" : "Not In Use"}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Status:</span>
                <span className="text-green-500 font-medium">
                  {enrollment?.status}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Plan:</span>
                <span>
                  {enrollment?.hubPlan?.tier?.name || ""}
                  {enrollment?.hubPlan?.name || "N/A"}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Start Date:</span>
                <span>{formatDate(enrollment?.enrolledAt || "")}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>End Date:</span>
                <span>
                  {enrollment?.expiresAt
                    ? formatDate(enrollment?.expiresAt)
                    : "Ongoing"}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Total Hours:</span>
                <span>{enrollment?.totalHours} hours</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Hours Used:</span>
                <span>{calculateHoursUsed()} hours</span>
              </div>

              <div className="rounded-md bg-muted p-3 text-xs text-muted-foreground">
                Note: Usage hours are calculated based on actual time spent,
                with a minimum billing duration applied when applicable.
              </div>

              <div className="flex justify-between text-sm">
                <span>Hours Remaining:</span>
                <span className="font-medium">
                  {(enrollment?.totalHours || 0) +
                    calculateTotalAdjustedHours() -
                    calculateHoursUsed()}{" "}
                  hours
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Adjusted Hours:</span>
                <span className="text-green-500 font-medium">
                  {(enrollment?.totalHours || 0) +
                    calculateTotalAdjustedHours() -
                    calculateHoursUsed()}{" "}
                  hours{" "}
                </span>
              </div>
            </CardContent>
            <CardFooter>
              <Button variant="outline" className="w-full">
                View Detailed Usage →
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>

        {/* -------- HOUR ADJUSTMENTS TAB -------- */}
        {showHourAdjustments && (
          <TabsContent value="adjustments" className="mt-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold text-lg">Hour Adjustments</h3>
              <div className="flex justify-between items-center mb-3 gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    if (canReadHourAdjustments) {
                      refetchAdjustments();
                    }
                  }}
                >
                  Refresh
                </Button>
                {canCreateHourAdjustments && (
                  <Button
                    onClick={() => {
                      form.setValue("editingId", "new");
                      form.setValue("hours", 0);
                      form.setValue("reason", "");
                      setOpenDialog(true);
                      setEditItem(null);
                    }}
                  >
                    Add Adjustment +
                  </Button>
                )}
              </div>
            </div>

            {hourAdjustments?.length === 0 ? (
              <h2 className="text-2xl font-bold mb-2">
                No hour adjustments yet.
              </h2>
            ) : (
              <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                {hourAdjustments?.map((item) => (
                  <Card
                    key={item.id}
                    className="shadow-md hover:shadow-lg transition"
                  >
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-center">
                        <span
                          className={`font-semibold ${
                            item.hours >= 0 ? "text-green-600" : "text-red-500"
                          }`}
                        >
                          {item.hours >= 0 ? `+${item.hours}` : item.hours}{" "}
                          hours
                        </span>
                        <span className="text-sm text-muted-foreground">
                          {formatDate(item.createdAt || item.updatedAt)}
                        </span>
                      </div>
                    </CardHeader>

                    <CardContent>
                      <p className="text-sm text-foreground break-words">
                        {item.reason || "No reason provided"}
                      </p>
                    </CardContent>

                    <CardFooter className="flex justify-between pt-3">
                      <span>
                        By:{" "}
                        {item.createdBy?.firstName ||
                          item.createdBy?.firstName ||
                          "Unknown"}
                      </span>
                      <div className="flex gap-2">
                        {canUpdateHourAdjustments && (
                          <Button
                            variant="outline"
                            size="icon"
                            className=" hover:bg-destructive/10"
                            onClick={() => openAdjustmentModal(item)}
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        )}
                        {canDeleteHourAdjustments && (
                          <Button
                            variant="outline"
                            size="icon"
                            className="text-destructive hover:bg-destructive/10"
                            onClick={() =>
                              deleteAdjustmentMutation.mutate({
                                id: item.id,
                                enrollmentId: enrollmentId!,
                              })
                            }
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* -------- ALERT DIALOG (ADD/EDIT) -------- */}
      {showHourAdjustments && (
        <AlertDialog open={openDialog} onOpenChange={setOpenDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {editItem ? "Edit Hour Adjustment" : "Add Hour Adjustment"}
              </AlertDialogTitle>
              <AlertDialogDescription>
                Hours (+ for bonus, - for reduction)
              </AlertDialogDescription>
            </AlertDialogHeader>
            <Form {...form}>
              <form className="space-y-4 py-4" onSubmit={handleSubmit}>
                <FormField
                  control={control}
                  name="hours"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Hours *</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter Hours" {...field} />
                      </FormControl>
                      <FormMessage className="text-left" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="reason"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Reason *</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Enter reason"
                          className="min-h-[120px]"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage className="text-left" />
                    </FormItem>
                  )}
                />
              </form>
            </Form>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                type="submit"
                disabled={isSubmitting}
                onClick={handleSubmit}
              >
                {isSubmitting ? "Saving..." : "Save"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}
